// ENG-4712: persist the manager's channel-config hash cache across
// process restarts.
//
// The manager keeps an in-memory Map of `agentId:channelId → sha256` so
// that consecutive polls don't re-write `.mcp.json` channel entries
// that haven't changed. That Map lives at module scope, so every fresh
// manager process (post self-update on Linux EC2, post launchd respawn
// on macOS, post SIGTERM, etc.) starts empty. With an empty cache the
// very next poll re-emits a `Channel credentials written … reason=first-write`
// log line for every active agent × channel pair, even though the
// on-disk content already matches the desired config — a noisy and
// misleading signal that operators read as "credentials keep churning".
//
// Round-tripping the Map through a JSON sidecar at the configDir root
// turns those churn lines into silent no-ops on restart while leaving
// the existing in-memory cache contract unchanged.

import { existsSync, readFileSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';

const CACHE_FILENAME = 'channel-hash-cache.json';

export function getChannelHashCacheFile(configDir: string): string {
  return join(configDir, CACHE_FILENAME);
}

/**
 * Hydrate `target` with persisted entries. Missing / unreadable /
 * malformed files are treated as "empty cache" — the next poll will
 * rewrite credentials and repopulate, which costs at most one extra
 * round of `first-write` log lines but never breaks correctness.
 */
export function loadChannelHashCache(
  target: Map<string, string>,
  configDir: string,
): void {
  const path = getChannelHashCacheFile(configDir);
  if (!existsSync(path)) return;
  let parsed: unknown;
  try {
    parsed = JSON.parse(readFileSync(path, 'utf-8'));
  } catch {
    return;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return;
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof value === 'string' && value.length > 0) target.set(key, value);
  }
}

/**
 * Persist `source` to disk. Failures are non-fatal — the cache stays
 * in memory and the next save attempt may succeed. We accept one extra
 * `first-write` round on the next manager restart over crashing the
 * poll loop.
 */
export function saveChannelHashCache(
  source: Map<string, string>,
  configDir: string,
): void {
  const path = getChannelHashCacheFile(configDir);
  const obj: Record<string, string> = {};
  for (const [key, value] of source) obj[key] = value;
  try {
    writeFileSync(path, JSON.stringify(obj, null, 2));
  } catch {
    // intentional no-op; see doc comment
  }
}
